import { useCallback, useEffect, useRef, useState } from "react";

interface UseCalendarEditModeOptions {
  setCalendarDirty: (dirty: boolean) => void;
}

export function useCalendarEditMode({ setCalendarDirty }: UseCalendarEditModeOptions) {
  const [calendarEditMode, setCalendarEditMode] = useState(false);
  const [openBlockModalTick, setOpenBlockModalTick] = useState(false);
  const [specificDayDate, setSpecificDayDate] = useState<string | null>(null);
  const [isTogglingEdit, setIsTogglingEdit] = useState(false);
  const [isExitingEdit, setIsExitingEdit] = useState(false);

  const [scheduleHasChanges, setScheduleHasChanges] = useState(false);
  const [specificDayHasChanges, setSpecificDayHasChanges] = useState(false);
  const [blockHasChanges, setBlockHasChanges] = useState(false);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingCloseCallback, setPendingCloseCallback] = useState<(() => void) | null>(null);

  const configDropdownRef = useRef<HTMLDivElement>(null);

  const handleScheduleHasChangesChange = useCallback((v: boolean) => {
    setScheduleHasChanges(v);
  }, []);

  const handleCloseBlockModal = () => {
    setOpenBlockModalTick(false);
    setCalendarEditMode(false);
  };

  // Sync dirty state with AppShell for tab navigation protection
  useEffect(() => {
    setCalendarDirty(
      calendarEditMode && (scheduleHasChanges || specificDayHasChanges || blockHasChanges),
    );
    return () => setCalendarDirty(false);
  }, [calendarEditMode, scheduleHasChanges, specificDayHasChanges, blockHasChanges, setCalendarDirty]);

  // Warn on browser tab close with unsaved changes
  useEffect(() => {
    if (!calendarEditMode || (!scheduleHasChanges && !specificDayHasChanges && !blockHasChanges))
      return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [calendarEditMode, scheduleHasChanges, specificDayHasChanges, blockHasChanges]);

  // Handle cancel from SpecificDayPanel
  useEffect(() => {
    const handler = () => {
      setPendingCloseCallback(() => () => {
        setSpecificDayDate(null);
        setCalendarEditMode(false);
      });
      setShowUnsavedModal(true);
    };
    window.addEventListener("psi-specific-day-cancel", handler);
    return () => window.removeEventListener("psi-specific-day-cancel", handler);
  }, []);

  // Handle cancel from BlockSchedulePanel
  useEffect(() => {
    const handler = () => {
      setPendingCloseCallback(() => () => {
        setOpenBlockModalTick(false);
        setCalendarEditMode(false);
      });
      setShowUnsavedModal(true);
    };
    window.addEventListener("psi-block-cancel", handler);
    return () => window.removeEventListener("psi-block-cancel", handler);
  }, []);

  // Cerrar el panel de bloqueo sin preguntar: lo usa "Voy a trabajar" del aviso de feriados
  // (si decidió trabajar ese día, el panel de bloqueo que había abierto ya no tiene sentido).
  useEffect(() => {
    const handler = () => {
      setOpenBlockModalTick(false);
      setCalendarEditMode(false);
    };
    window.addEventListener("psi-block-close", handler);
    return () => window.removeEventListener("psi-block-close", handler);
  }, []);

  return {
    calendarEditMode,
    setCalendarEditMode,
    openBlockModalTick,
    setOpenBlockModalTick,
    specificDayDate,
    setSpecificDayDate,
    isTogglingEdit,
    setIsTogglingEdit,
    isExitingEdit,
    setIsExitingEdit,
    configDropdownRef,
    scheduleHasChanges,
    setScheduleHasChanges,
    specificDayHasChanges,
    setSpecificDayHasChanges,
    blockHasChanges,
    setBlockHasChanges,
    showUnsavedModal,
    setShowUnsavedModal,
    pendingCloseCallback,
    setPendingCloseCallback,
    handleScheduleHasChangesChange,
    handleCloseBlockModal,
  };
}
